import { providerApi } from '@/api/ProviderApi'
import { retryUnlessRefused } from '@/api/retryPolicy'
import { addWeeks } from '@/features/scheduling/availability'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

export const AVAILABILITY_QUERY_KEY = 'patient-availability'

type Params = { weekStart: string; geoId?: number; rescheduleAppointmentId?: number }

const availabilityKey = ({ weekStart, geoId, rescheduleAppointmentId }: Params) => [
	AVAILABILITY_QUERY_KEY,
	rescheduleAppointmentId ?? 'new',
	geoId ?? 'home',
	weekStart,
]

/**
 * One week of openings, with the weeks either side warmed.
 *
 * The previous week stays on screen while the next one loads, because a picker that blanks to a
 * skeleton on every arrow tap reads as slower than it is. Stepping between weeks is the main
 * thing a patient does here, and with no way to ask where availability actually is, they may do
 * it several times.
 */
const usePatientAvailability = (params: Params) => {
	const queryClient = useQueryClient()

	const { data, isLoading, isFetching, isError, refetch } = useQuery({
		queryKey: availabilityKey(params),
		queryFn: () => providerApi.fetchPatientAvailability(params),
		staleTime: 30 * 1000,
		retry: retryUnlessRefused,
		placeholderData: keepPreviousData,
	})

	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed by the week on screen
	useEffect(() => {
		if (!data) {
			return
		}
		for (const step of [-1, 1]) {
			const weekStart = addWeeks(params.weekStart, step)
			if (weekStart < data.minWeekStart || weekStart > data.maxWeekStart) {
				continue
			}
			const neighbour = { ...params, weekStart }
			queryClient.prefetchQuery({
				queryKey: availabilityKey(neighbour),
				queryFn: () => providerApi.fetchPatientAvailability(neighbour),
				staleTime: 30 * 1000,
			})
		}
	}, [data, params.weekStart, queryClient])

	return {
		availability: data,
		isLoading,
		/** True while a different week is on its way, with the last one still drawn. */
		isChangingWeek: isFetching && !isLoading,
		isError,
		refetch,
	}
}

export { usePatientAvailability }
